// External Imports
import { Fragment, useContext, useEffect, useState } from "react";
import Heading from "../UI/Heading";
import { MdAdd } from "react-icons/md";
import { UtilityContext } from "../../context/util-context";
import AddMaintenance from "./AddMaintenanceModal";
import { requestAdminAPI } from "../../api/posh-api";
import { useLocation } from "react-router-dom";
import DateFilter from "../UI/DateFilter";
import MaintenanceCard from "./MaintenanceCard";
import ExportCsvButton from "../UI/ExportCsvButton";
import SearchBar from "../UI/SearchBar";
import Radio from "../UI/Radio";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import PaginationMenu from "../UI/PaginationMenu";
import FilterMenu from "../UI/FilterMenu";
import { endOfMonth, startOfMonth } from "date-fns";

// Internal Imports

/** ========================================================================
 * * Maintenance Component
 * This component renders a page to manage vehicle maintenance.
 * ========================================================================== */

export default function Maintenance() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    status: "all",
    query: "",
    sd: "",
    ed: "",
    date: "dateCreated",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [logs, setLogs] = useState([]);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(paramFilters);
  const [add, setAdd] = useState(false);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const res = await requestAdminAPI("search", {
        table: "maintenance",
        limit: limit,
        filters: filter,
        columns: ["vehicle { year, model, vin, image: cornerImage }"],
      });

      // Sort Results by Date Created
      res.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

      // Replace URL
      let url = "/maintenance?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setLogs(res);
      setLoading(false);
    } catch (err) {
      console.error(err);

      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Date",
      options: [
        { value: "dateCreated", label: "Date Reported" },
        { value: "dateDiagnosed", label: "Date Diagnosed" },
        { value: "dateProgress", label: "Date Started" },
        { value: "dateCompleted", label: "Date Completed" },
      ],
      selected: filter.date,
    },
  ];

  // Fetch Records on Load
  useEffect(() => {
    fetchRecords();
  }, [filter, limit]);

  return (
    <div className="container grid gap-4">
      <AddMaintenance open={add} setOpen={setAdd} onComplete={fetchRecords} />
      <Heading
        title="Maintenance"
        isBackArrow={true}
      >
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          options={filterOptions}
          onSubmit={(f) => setFilter({ ...filter, f })}
        />
        <MdAdd
          className="text-3xl icon"
          title="Report Issue"
          onClick={() => setAdd(true)}
        />
        <ExportCsvButton array={logs} filename="maintenance" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      <div className="grid grid-cols-5 gap-4 h-fit">
        <Radio
          selected={filter.status}
          options={[
            { id: "all", name: "All" },
            "Reported",
            "Diagnosed",
            { id: "in-progress", name: "In Progress" },
            "Completed",
          ]}
          onClick={(status) =>
            setFilter({ ...filter, status: status.toLowerCase() })
          }
        />
      </div>

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : logs.length > 0 ? (
        <div className="list">
          {logs.map((log, idx) => (
            <MaintenanceCard key={idx} item={log} />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={limit}
        setLimit={setLimit}
        loading={loading}
        count={logs.length}
      />
    </div>
  );
}
